/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import "./TruWebForm.scss";
import { DynamicFormContextProvider } from "./widgets/DynamicForm/DynamicFormContext";
import { DynamicForm } from "./widgets/DynamicForm";
import { useHistory } from "react-router-dom";
import { diff } from "deep-diff";
import { Stepper } from "./controls/Stepper";
import { cloneDeep, delay, get, isEmpty, merge } from "lodash";
import { useQParam } from "./hooks/useQParam";
import { DocumentLayout } from "./widgets/DocumentView";
import { Tooltip } from "./elements/Tooltip/index";
import { Button } from "./elements/Button/index";
import Cookies from "js-cookie";
import {
  useFetchRequest,
  useFetchRequestDocument,
  useInvokeAction,
  usePartialUpdate,
  useCreateRequest,
  useChangeStatus,
  useClientUpdate,
  useFetchUiSchema,
} from "./hooks/NetworkHooks";
import { useInterval } from "./hooks/useInterval";
import { message } from "antd";
import { ThankYouPage } from "./Pages/ThankYouLayout";
import { CaretUpOutLined } from "./icons/truWeb-icons";
import SchemaNotFound from "./elements/SchemaNotFound";

const brokerData = {
  B_CompanyName: Cookies.get("B_CompanyName")
    ? Cookies.get("B_CompanyName")
    : undefined,
  B_BrokerName: Cookies.get("B_BrokerName")
    ? Cookies.get("B_BrokerName")
    : undefined,
  B_Address: Cookies.get("B_Address") ? Cookies.get("B_Address") : undefined,
  B_City: Cookies.get("B_City") ? Cookies.get("B_City") : undefined,
  B_PostalCode: Cookies.get("B_PostalCode")
    ? Cookies.get("B_PostalCode")
    : undefined,
  B_Email: Cookies.get("B_Email") ? Cookies.get("B_Email") : undefined,
  B_Phone: Cookies.get("B_Phone") ? Cookies.get("B_Phone") : undefined,
  Elite_Code: Cookies.get("Elite_Code") ? Cookies.get("Elite_Code") : undefined,
};

const LoadingWrapper = () => {
  return (
    <div className="tru-list-loading">
      <span className="loading-anim"></span>
    </div>
  );
};

const TruWebForm = () => {
  const thankyouId = useQParam("thank-you");
  const history = useHistory();
  const requestKey = useQParam("requestKey");
  const [isLoading, setIsLoading] = useState(true);
  const [isSchemaLoading, setIsSchemaLoading] = useState(false);
  const [requestData, setRequestData] = useState({ data: {} });
  const [dataObj, setDataObj] = useState({
    quoteHasPremium: false,
    quoteHasError: false,
    mustRequote: false,
    stepNum: 0,
    applicant: { ...brokerData },
  });
  const [validationFlag, setValidationFlag] = useState(false);
  const [requestDocument, setRequestDocument] = useState(null);
  const [isActionsPending, setIsActionsPending] = useState({});
  const [schemaObj, setSchema] = useState({
    schema: {},
    uiSchema: {},
  });
  const [locationKeys, setLocationKeys] = useState([]);
  // Getting data-key attribute value from '#t-trumarket'
  const {
    key: requestDefinitionKey,
    uischematype,
    source,
  } = document.querySelector("#t-trumarket").dataset;

  const invokeAction = useInvokeAction();
  const fetchRequest = useFetchRequest();
  const createRequest = useCreateRequest();
  const fetchRequestDocument = useFetchRequestDocument();
  const partialUpdate = usePartialUpdate();
  const updateClient = useClientUpdate();
  const changeStatus = useChangeStatus();
  const fetchUiSchema = useFetchUiSchema();

  const handleCreateRequestInstance = useCallback(async () => {
    if (requestKey) return null;
    try {
      const requestResponse = await createRequest({
        data: { source: source || "truWeb" },
        request_definition_key: requestDefinitionKey,
      });

      Object.keys(brokerData).forEach(
        (key) => brokerData[key] === undefined && delete brokerData[key]
      );

      if (!isEmpty(brokerData)) {
        handlePartialUpdate(
          { applicant: { ...brokerData }, stepNum: 0 },
          requestResponse.data.key
        );
      }
      history.push({ search: `?requestKey=${requestResponse.data.key}` });
    } catch (error) {
      message.error("Something Went Wrong!");
      setIsLoading(false);
    }
  }, []);

  const fetchRequestData = async (change, initial) => {
    if (!requestKey) return false;

    try {
      const requestDataResponse = await fetchRequest(requestKey, uischematype);

      let dataFromTrueMarket = {};

      const {
        data: {
          requestDefinition: { dataSchema },
          uiSchema: { schema },
        },
      } = requestDataResponse;

      const prevData = cloneDeep(dataObj);
      dataFromTrueMarket = merge(prevData, {
        ...requestDataResponse.data.data,
        stepNum:
          initial || change
            ? requestDataResponse?.data?.data?.stepNum
            : dataObj.stepNum,
        mustRequote: change ? false : dataObj.mustRequote,
      });

      Object.keys(dataFromTrueMarket.applicant).forEach(
        (key) =>
          dataFromTrueMarket.applicant[key] === undefined &&
          delete dataFromTrueMarket.applicant[key]
      );

      if (initial) {
        // handling refresh in case of user press back button after getting quote.
        // cause ui schema will not be updated back to applicant type after press back button.
        if (
          !requestDataResponse?.data?.data?.quoteHasPremium &&
          requestDataResponse?.data?.data?.successStatusKeys.includes(
            requestDataResponse?.data?.status?.key
          )
        ) {
          setSchema({ schema: dataSchema });
          handleFetchUiSchema(requestDataResponse.data.applicationUiSchemaKey);
        }
        // handling if user removed thankyouId query param
        else if (requestDataResponse?.data?.data?.quoteHasError) {
          setSchema({ schema: dataSchema });
          handleCheckQuoteStatus(requestDataResponse.data, schema);
        }
        // handling if user removed thankyouId query param after request call clicked
        else if (
          requestDataResponse?.data?.data?.quoteHasPremium &&
          requestDataResponse?.data?.data?.requestCallClicked
        ) {
          setSchema({ schema: dataSchema });
          handleNavigate({
            search: "thank-you=success",
            state:
              "Your estimate is based on the information you entered into the system and we are confident that it has been calculated accurately based on your unique information. Before insurance coverage can be issued, Underwriters must review and approve a completed application.  We will contact you within the day, if submitted during business hours, and on the next business day, if submitted after hours. Please be prepared to provide the motorcycle's VIN when we contact you.",
          });
        } else setSchema({ schema: dataSchema, uiSchema: schema });
      }

      setRequestData({
        ...requestDataResponse?.data,
        data: dataFromTrueMarket,
      });
      setDataObj(dataFromTrueMarket);

      if (change && !handleCheckQuoteStatus(requestDataResponse.data))
        fetchRequestData(true, false);

      setIsLoading(false);
    } catch (error) {
      message.error("Something Went Wrong!");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleCreateRequestInstance();
  }, [handleCreateRequestInstance]);

  useEffect(() => {
    fetchRequestData(null, true);
  }, [requestKey]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [dataObj.stepNum]);

  useEffect(() => {
    return history.listen((location) => {
      if (history.action === "PUSH") {
        setLocationKeys([location.key]);
      } else if (history.action === "POP") {
        history.go(1);
        if (locationKeys[1] === location.key) {
          setLocationKeys(([_, ...keys]) => keys);
        } else {
          setLocationKeys((keys) => [location.key, ...keys]);
        }
      }
    });
  }, [locationKeys]);

  // to fetch request data from trumarket every minute
  useInterval(() => {
    if (
      dataObj.quotes &&
      dataObj.quotes[0]?.status === "bound" &&
      !requestDocument
    )
      handleFetchRequestDocument();
  }, 60000);

  const handleFetchRequestDocument = async () => {
    if (!requestKey || requestDocument?.length > 0) return false;
    try {
      handleChangeIsActionsPending({ fetchDocument: true });
      const requestDocumentResponse = await fetchRequestDocument(requestKey);
      setRequestDocument(requestDocumentResponse.data);
      handleChangeIsActionsPending({ fetchDocument: false }, true);
    } catch (error) {
      message.error("Something Went Wrong!");
      handleChangeIsActionsPending({ fetchDocument: false }, true);
    }
  };

  const handlePartialUpdate = async (data, requestDataKey, isInvoke) => {
    if (!requestKey && !requestDataKey) return false;

    const prevData = {
      ...requestData.data,
      stepNum: isInvoke
        ? requestData?.data?.stepNum + 1
        : requestData?.data?.stepNum,
    };

    const diffs = diff(prevData, data);

    if (!diffs?.length) return true;

    const sortedDiffs = diffs.sort((a, b) => a.index - b.index);

    let body = {
      data: sortedDiffs,
    };

    const res = await partialUpdate(
      body,
      requestKey ? requestKey : requestDataKey
    );
    if (res) setRequestData((prev) => ({ ...prev, data }));
    else setIsSchemaLoading(false);
    return res;
  };

  const handleInvokeAction = async (actionKey, data, loading) => {
    if (!requestKey) return false;
    try {
      handleChangeIsActionsPending({ invoke: true });
      await invokeAction({ actionKey, data: { requestKey, ...data } });
      handleChangeIsActionsPending({ invoke: false }, true);
    } catch (err) {
      handleChangeIsActionsPending({ invoke: false }, true);
      setDataObj(dataObj);
      handlePartialUpdate(dataObj, null, true);
      setIsSchemaLoading(false);
      message.error("Something went wrong , please try again");
    }
  };

  const handleStartLoading = () => {
    setIsSchemaLoading(true);
    delay(() => {
      fetchRequestData(true, false);
    }, 7000);
  };

  const handleFetchUiSchema = async (uiSchemaKey) => {
    if (!requestKey) return false;
    setIsSchemaLoading(true);
    try {
      const { data } = await fetchUiSchema(uiSchemaKey, requestDefinitionKey);
      setSchema((prev) => ({ ...prev, uiSchema: data.schema }));
      setIsSchemaLoading(false);
    } catch (error) {
      message.error("Something went wrong , please try again");
    }
  };

  const handleNavigate = (payload, reload) => {
    if (payload.search || payload.search === "") {
      if (!requestKey) history.push(payload);
      else if (payload.search === "")
        history.push({
          search: `?requestKey=${requestKey}`,
          state: payload.state,
          pathname: payload.pathname,
        });
      else
        history.push({
          search: `?requestKey=${requestKey}&${payload.search}`,
          state: payload.state,
          pathname: payload.pathname,
        });
    } else {
      history.push(payload);
      if (reload) window.location.reload();
    }
  };

  const handleNavigateWithCondition = (payload, condition, options) => {
    const value = get(dataObj, condition.path);
    if (value) {
      if (options.paymentPaths && !sessionStorage.getItem("paymentPaths")) {
        sessionStorage.setItem(
          "paymentPaths",
          JSON.stringify(options.paymentPaths)
        );
      }
      if (options?.redirect && payload?.redirectParams) {
        let search = payload.search ? `${payload.search}&` : "";
        for (const key in payload?.redirectParams) {
          search += `${key}=${payload?.redirectParams[key]}&`;
        }
        handleNavigate({
          ...payload,
          search: search.replaceAll("$href", window.location.href),
        });
      } else handleNavigate(payload);
      if (options?.reload) window.location.reload();
    }
  };

  const handleReturnBack = () => {
    if (thankyouId === "failed") {
      const newData = {
        ...dataObj,
        stepNum: dataObj.returnBackStepNum,
        quoteHasError: false,
        requestCallClicked: false,
        insureMe: false,
      };
      setDataObj(newData);
      handlePartialUpdate(newData);
      handleNavigate({ search: "", state: "" });
    } else {
      handleNavigate({ search: "", state: "" });
    }
  };

  const handleUpdateClient = async (data, actionData) => {
    if (!requestKey) return false;
    let name = `${get(data, actionData.path[0])} ${get(
      data,
      actionData.path[1]
    )}`;

    let email = get(data, actionData.path[2]);
    let phoneNumber = get(data, actionData.path[3]);

    try {
      await updateClient({ name: name.trim(), email, phoneNumber }, requestKey);
    } catch (error) {
      message.error("Something went wrong , please try again");
    }
  };

  const handleChangeStatus = async (data) => {
    if (!requestKey) return false;
    try {
      handleChangeIsActionsPending({ changeStatus: true });
      await changeStatus({ ...data, requestInstanceKey: requestKey });
      handleChangeIsActionsPending({ changeStatus: false }, true);
    } catch (error) {
      message.error("Something went wrong , please try again");
      handleChangeIsActionsPending({ changeStatus: false }, true);
    }
  };

  const handleCheckQuoteStatus = (data, uiSchema) => {
    let isStatusChanged = false;
    if (
      data?.data?.successStatusKeys.includes(data?.status?.key) &&
      data?.data?.quoteHasPremium
    ) {
      setSchema((prev) => ({
        ...prev,
        uiSchema: data?.uiSchema?.schema,
      }));
      setIsSchemaLoading(false);
      isStatusChanged = true;
    } else if (
      data?.data?.failedStatusKeys.includes(data?.status?.key) &&
      data?.data?.quoteHasError
    ) {
      setSchema((prev) => ({
        ...prev,
        uiSchema: data?.uiSchema?.schema,
      }));
      handleNavigate({
        search: "thank-you=failed",
        state:
          schemaObj?.uiSchema?.options?.thankyouMessage ||
          uiSchema?.options?.thankyouMessage,
      });
      setIsSchemaLoading(false);
      isStatusChanged = true;
    }
    return isStatusChanged;
  };

  const handleChangeIsActionsPending = (data, isDelay) => {
    if (isDelay)
      delay(() => {
        setIsActionsPending((prev) => ({ ...prev, ...data }));
      }, 2000);
    else setIsActionsPending((prev) => ({ ...prev, ...data }));
  };

  if (isLoading || isSchemaLoading) {
    return <LoadingWrapper />;
  }

  if (isEmpty(schemaObj.uiSchema)) {
    return <SchemaNotFound />;
  }

  return (
    <>
      {thankyouId && (
        <ThankYouPage
          options={schemaObj?.uiSchema?.options}
          data={dataObj}
          isSchemaLoading={isSchemaLoading}
          onReturnBack={handleReturnBack}
        />
      )}
      {dataObj.stepNum === 5 && (
        <DocumentLayout
          requestDocument={requestDocument}
          data={dataObj}
          uiSchema={schemaObj.uiSchema}
        />
      )}
      {!thankyouId && dataObj.stepNum !== 5 && (
        <>
          {isSchemaLoading && <LoadingWrapper />}
          <div className="truweb_form">
            {(dataObj.showStepperAtStart || dataObj.stepNum > 0) &&
              dataObj.stepNum < 5 &&
              schemaObj?.uiSchema && (
                <Stepper
                  steps={schemaObj?.uiSchema?.options?.steps}
                  activeStepId={dataObj.stepNum}
                />
              )}
            <DynamicFormContextProvider
              data={dataObj}
              schema={schemaObj.schema}
              uiSchema={schemaObj.uiSchema}
              isActionsPending={isActionsPending}
              onNavigate={handleNavigate}
              onNavigateWithCondition={handleNavigateWithCondition}
              onMutate={(data) => {
                setDataObj(data);
              }}
              onDataUpdate={(data) => {
                setDataObj(data);
              }}
              onInvokeAction={handleInvokeAction}
              onChangeStatus={handleChangeStatus}
              onStartLoading={handleStartLoading}
              onActionsPending={handleChangeIsActionsPending}
              onFetchUiSchema={handleFetchUiSchema}
              onPartialDataUpdate={(data) => handlePartialUpdate(data)}
              onUpdateClient={(data, actionData) =>
                handleUpdateClient(data, actionData)
              }
              onErrorsUpdate={(errors) => {
                if (!errors || errors?.length === 0) setValidationFlag(false);
              }}
              onActionError={() => {
                setValidationFlag(true);
              }}
              onFetchDocumentData={handleFetchRequestDocument}
              useInvokeAction={useInvokeAction}
            >
              <DynamicForm />
            </DynamicFormContextProvider>

            {validationFlag ? (
              <div className="show-errors-btn">
                <Tooltip title="Scroll to errors" position="left center">
                  <Button
                    onClick={() => {
                      document
                        .querySelector(".input-has-error")
                        ?.scrollIntoView({
                          block: "center",
                          behavior: "smooth",
                        });
                    }}
                  >
                    <CaretUpOutLined />
                  </Button>
                </Tooltip>
              </div>
            ) : null}
          </div>
        </>
      )}
    </>
  );
};

export default TruWebForm;
